
import React, { useContext, useEffect, useState } from "react";
import SectionCard from "../components/SectionCard";
import { Label, Input, Select } from "../components/FormControls";
import { Wallet } from "lucide-react";
import { toast } from "react-toastify";
import { GlobalContext } from "../../../../Context/GlobalContext";

import useOrderSettings from "../../../Services/OrderServices/OrderSettingsServices/useOrderSettings";

const EMPTY_FORM = {
  fromAmount: "",
  toAmount: "",
  charges: "",
  chargesType: "",
  chargesOn: "",
  orderType: "",
};

const ORDER_TYPES = [
  {
    value: "home_delivery",
    label: "Home Delivery",
  },
  {
    value: "takeaway",
    label: "Takeaway",
  },
  {
    value: "dining",
    label: "Dining",
  },
  {
    value: "car_order",
    label: "Car Order",
  },
];

const normalizeOrderType = (value) => {
  const normalizedValue = String(value ?? "")
    .trim()
    .toLowerCase()
    .replaceAll(" ", "_")
    .replaceAll("-", "_");

  if (
    normalizedValue === "homedelivery" ||
    normalizedValue === "home_delivery" ||
    normalizedValue === "delivery"
  ) {
    return "home_delivery";
  }

  if (
    normalizedValue === "dinein" ||
    normalizedValue === "dine_in" ||
    normalizedValue === "dining"
  ) {
    return "dining";
  }

  if (
    normalizedValue === "carorder" ||
    normalizedValue === "car_order"
  ) {
    return "car_order";
  }

  if (
    normalizedValue === "take_away" ||
    normalizedValue === "takeaway"
  ) {
    return "takeaway";
  }

  return normalizedValue;
};

const formatAmount = (value) => {
  const amount = Number(value);

  if (!Number.isFinite(amount)) {
    return "0.00";
  }

  return amount.toFixed(2);
};

const formatText = (value) => {
  if (value === null || value === undefined || value === "") {
    return "—";
  }

  return String(value)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export default function OrderChargesSection({ userID }) {
  const { getOrderChargesConfig, insertOrderChargesConfig } =
    useOrderSettings();

  const { adminId } = useContext(GlobalContext);

  const [list, setList] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    if (!userID) {
      setList([]);
      return;
    }

    try {
      setLoading(true);

      const result = await getOrderChargesConfig(userID);

      if (result?.success) {
        const responseList = Array.isArray(result?.data)
          ? result.data
          : [];

        const normalizedList = responseList.map((item) => ({
          ...item,

          order_type: normalizeOrderType(item?.order_type),
        }));

        setList(normalizedList);
      } else {
        setList([]);
      }
    } catch (error) {
      console.error("getOrderChargesConfig error:", error);
      setList([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userID) {
      setList([]);
      return;
    }

    load();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userID]);

  const updateForm = (field, value) => {
    setForm((previousForm) => ({
      ...previousForm,
      [field]: value,
    }));
  };

  const save = async () => {
    if (!userID) {
      toast.error("Client user ID not found");
      return;
    }

    if (!adminId) {
      toast.error("Admin ID not found");
      return;
    }

    if (
      !form.fromAmount ||
      !form.toAmount ||
      !form.charges ||
      !form.chargesType ||
      !form.chargesOn ||
      !form.orderType
    ) {
      toast.error("Fill all fields");
      return;
    }

    const fromAmount = Number(form.fromAmount);
    const toAmount = Number(form.toAmount);
    const charges = Number(form.charges);

    if (
      !Number.isFinite(fromAmount) ||
      !Number.isFinite(toAmount) ||
      !Number.isFinite(charges)
    ) {
      toast.error("Enter valid numeric values");
      return;
    }

    if (fromAmount < 0 || toAmount < 0 || charges < 0) {
      toast.error("Amount cannot be negative");
      return;
    }

    if (fromAmount > toAmount) {
      toast.error("From amount cannot be greater than to amount");
      return;
    }

    try {
      setSaving(true);

      const result = await insertOrderChargesConfig({
        userid: userID,
        adminId,
        fromAmount: form.fromAmount,
        toAmount: form.toAmount,
        charges: form.charges,
        chargesType: form.chargesType,
        chargesOn: form.chargesOn,
        orderType: form.orderType,
      });

      if (result?.success) {
        toast.success(result?.message || "Order charges saved");

        setForm(EMPTY_FORM);
        await load();
      } else {
        toast.error(result?.message || "Failed to save order charges");
      }
    } catch (error) {
      console.error("insertOrderChargesConfig error:", error);
      toast.error("Failed to save order charges");
    } finally {
      setSaving(false);
    }
  };

  const hasCharges = Array.isArray(list) && list.length > 0;

  return (
    <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
      <div className="space-y-6 lg:col-span-2">
        {loading ? (
          <SectionCard icon={Wallet} title="Order Charges">
            <div className="flex min-h-32 items-center justify-center">
              <p className="text-sm text-gray-500">
                Loading order charges...
              </p>
            </div>
          </SectionCard>
        ) : (
          <>
            {ORDER_TYPES.map((type) => {
              const rows = list.filter(
                (item) =>
                  normalizeOrderType(item?.order_type) === type.value,
              );

              if (!rows.length) {
                return null;
              }

              return (
                <SectionCard
                  key={type.value}
                  icon={Wallet}
                  title={`${type.label} Charges`}
                >
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                    {rows.map((row, index) => (
                      <div
                        key={
                          row?.pk_charge_id ||
                          `${type.value}-${index}`
                        }
                        className="rounded-xl border border-gray-200 bg-white p-4 text-sm shadow-sm"
                      >
                        <div className="grid grid-cols-2 gap-x-4 gap-y-5">
                          <div>
                            <div className="font-medium text-gray-900">
                              ₹{formatAmount(row?.from_amount)}
                            </div>

                            <div className="mt-1 text-xs text-gray-500">
                              From
                            </div>
                          </div>

                          <div>
                            <div className="font-medium text-gray-900">
                              ₹{formatAmount(row?.to_amount)}
                            </div>

                            <div className="mt-1 text-xs text-gray-500">
                              To
                            </div>
                          </div>

                          <div>
                            <div className="font-medium text-gray-900">
                              {row?.charge_type === "percentage"
                                ? `${formatAmount(row?.charges)}%`
                                : `₹${formatAmount(row?.charges)}`}
                            </div>

                            <div className="mt-1 text-xs text-gray-500">
                              Charges
                            </div>
                          </div>

                          <div>
                            <div className="font-medium text-gray-900">
                              {formatText(row?.charges_on)}
                            </div>

                            <div className="mt-1 text-xs text-gray-500">
                              Charges On
                            </div>
                          </div>

                          <div>
                            <div className="font-medium text-gray-900">
                              {formatText(row?.payment_method)}
                            </div>

                            <div className="mt-1 text-xs text-gray-500">
                              Payment
                            </div>
                          </div>

                          <div>
                            <div
                              className={`font-semibold ${
                                row?.status === "Y"
                                  ? "text-emerald-600"
                                  : "text-rose-600"
                              }`}
                            >
                              {row?.status === "Y"
                                ? "Active"
                                : "Inactive"}
                            </div>

                            <div className="mt-1 text-xs text-gray-500">
                              Status
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </SectionCard>
              );
            })}

            {!hasCharges && (
              <SectionCard icon={Wallet} title="Order Charges">
                <div className="flex min-h-32 items-center justify-center">
                  <p className="text-sm text-gray-500">
                    No order charges configuration found.
                  </p>
                </div>
              </SectionCard>
            )}
          </>
        )}
      </div>

      <div>
        <SectionCard
          icon={Wallet}
          title="Add / Update Order Charges"
        >
          <div className="space-y-3">
            <div>
              <Label>From Amount</Label>

              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.fromAmount}
                onChange={(event) =>
                  updateForm("fromAmount", event.target.value)
                }
                placeholder="Enter from amount"
              />
            </div>

            <div>
              <Label>To Amount</Label>

              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.toAmount}
                onChange={(event) =>
                  updateForm("toAmount", event.target.value)
                }
                placeholder="Enter to amount"
              />
            </div>

            <div>
              <Label>Charges</Label>

              <Input
                type="number"
                min="0"
                step="0.01"
                value={form.charges}
                onChange={(event) =>
                  updateForm("charges", event.target.value)
                }
                placeholder="Enter charges"
              />
            </div>

            <div>
              <Label>Charges Type</Label>

              <Select
                value={form.chargesType}
                onChange={(event) =>
                  updateForm("chargesType", event.target.value)
                }
              >
                <option value="">Select</option>
                <option value="fixed">Fixed</option>
                <option value="percentage">Percentage</option>
              </Select>
            </div>

            <div>
              <Label>Charges On</Label>

              <Select
                value={form.chargesOn}
                onChange={(event) =>
                  updateForm("chargesOn", event.target.value)
                }
              >
                <option value="">Select</option>
                <option value="message">Per Message</option>
                <option value="order">Per Order</option>
              </Select>
            </div>

            <div>
              <Label>Order Type</Label>

              <Select
                value={form.orderType}
                onChange={(event) =>
                  updateForm("orderType", event.target.value)
                }
              >
                <option value="">Select</option>

                <option value="homedelivery">
                  Home Delivery
                </option>

                <option value="takeaway">
                  Takeaway
                </option>

                <option value="dining">
                  Dining
                </option>

                <option value="car_order">
                  Car Order
                </option>
              </Select>
            </div>

            <div className="pt-1">
              <button
                type="button"
                onClick={save}
                disabled={saving}
                className="w-full rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? "Saving..." : "Save"}
              </button>
            </div>
          </div>
        </SectionCard>
      </div>
    </div>
  );
}
